<template>
    <vx-table-row>
        <vx-table-row-title>
            {{ props.modelValue.target_name }}
        </vx-table-row-title>

        <vx-table-row-item>
            <template v-slot:label> Type: </template>
            <vx-badge v-if="props.modelValue.target_type === 'org_unit'" color="violet" size="xs">
                Org
                <font-awesome-icon widthAuto :icon="faBuildingFlag" class="text-violet-500"></font-awesome-icon>
            </vx-badge>
            <vx-badge v-if="props.modelValue.target_type === 'group'" color="slate" size="xs">
                Group
                <font-awesome-icon widthAuto :icon="faGrid2Plus" class="text-slate-500"></font-awesome-icon>
            </vx-badge>
        </vx-table-row-item>

        <vx-table-row-item>
            <template v-slot:label> OU: </template>
            <vx-icon-loading v-if="loadingOu" spin></vx-icon-loading>
            <span v-else-if="ouName">{{ ouName }}</span>
            <span v-else>-</span>
        </vx-table-row-item>

        <vx-table-row-item>
            <template v-slot:label> Users: </template>
            <vx-icon-loading v-if="loadingUsers" spin></vx-icon-loading>
            <span v-else>{{ usersCount }}</span>
        </vx-table-row-item>

        <vx-table-row-buttons>
            <vx-button data-test="remove" type="button" color="muted" outline @click="removeTarget()">
                Remove <font-awesome-icon widthAuto :icon="faCircleMinus" />
            </vx-button>
        </vx-table-row-buttons>
    </vx-table-row>
</template>

<script setup lang="ts">
import { faBuildingFlag, faCircleMinus, faGrid2Plus } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import {
    VxBadge,
    VxButton,
    VxIconLoading,
    VxSwal,
    VxTableRow,
    VxTableRowButtons,
    VxTableRowItem,
    VxTableRowTitle,
} from '@voxie/frontend-components';
import { ref, computed, onMounted } from 'vue';
import { groupsClient, groupUsersClient, orgUnitUsersClient } from '~/services';
import { formatNumber } from '~/utils/helpers';
import type { TargetMappingType } from '~/services/messageStandards/messageStandardsClient.types';

interface TargetRowType extends TargetMappingType {
    team_id: number;
}

const props = defineProps<{ modelValue: TargetRowType; messageStandardId: number }>();

const emit = defineEmits<{
    remove: [{ target_type: 'group' | 'org_unit'; target_id: string | number }];
}>();

const isGroup = computed(() => props.modelValue.target_type === 'group');

const ouName = ref('');
const loadingOu = ref(false);
const usersCount = ref('0');
const loadingUsers = ref(false);

const fetchGroupOu = async () => {
    if (!isGroup.value || ouName.value || loadingOu.value) {
        return;
    }

    loadingOu.value = true;
    try {
        const { data } = await groupsClient.getGroup(
            { teamId: props.modelValue.team_id },
            String(props.modelValue.target_id)
        );
        ouName.value = data.org_unit?.name || '';
    } catch {
        ouName.value = '';
    } finally {
        loadingOu.value = false;
    }
};

const fetchUsersCount = async () => {
    loadingUsers.value = true;
    try {
        const request = isGroup.value
            ? groupUsersClient.countGroupUsers(props.modelValue.team_id, String(props.modelValue.target_id))
            : orgUnitUsersClient.countOrgUnitUsers(props.modelValue.team_id, String(props.modelValue.target_id));
        const { data } = await request;
        usersCount.value = formatNumber(data.count);
    } catch {
        usersCount.value = '0';
    } finally {
        loadingUsers.value = false;
    }
};

const removeTarget = async () => {
    const typeLabel = isGroup.value ? 'Group' : 'Organizational Unit';
    VxSwal.confirmation(
        `Remove ${typeLabel} from Brand Standard`,
        'Are you sure? There is no undo.',
        'danger',
        async () => {
            emit('remove', {
                target_type: props.modelValue.target_type,
                target_id: props.modelValue.target_id,
            });
        }
    );
};

onMounted(() => {
    if (isGroup.value) {
        fetchGroupOu();
    }
    fetchUsersCount();
});
</script>
